import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, LockKeyhole, Save, Settings } from 'lucide-react';
import { apiClient } from '../../api/apiClient';
import { useToast } from '../../components/Toast';
import { todayKST } from '../../utils/date';
import {
  MONEY_FIELDS, formatNumber, toDraft, MoneyInput, ReadMoney, confirmButtonState,
} from './promotionalMonthlySettlement.shared';
import PromotionalMonthlySettlementMobile from './PromotionalMonthlySettlementMobile';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle, stdOutlineBtn } from './adminlayout/designStandard';
import PromotionalMonthlyMagokModal from './PromotionalMonthlyMagokModal';
import './PromotionalMonthlySettlement.css';
import './PromotionalMonthlySettlementMobile.css'; // 휴대폰 카드 — 본 파일 다음에 불러 종전 순서 유지

const SETTLEMENT_START_MONTH = '2026-04';

export default function PromotionalMonthlySettlement() {
  const { showToast } = useToast();
  const currentYm = todayKST().slice(0, 7);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [magokOpen, setMagokOpen] = useState(false); // 마곡 마케터 설정 창 (2026-10-05)
  // 모바일(≤768px): 13컬럼 테이블 대신 월별 카드 (useSettlement.js 패턴)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get(`/api/admin/promotional-monthly-settlements?from=${SETTLEMENT_START_MONTH}&to=${currentYm}`);
      if (!result.ok) throw new Error(result.data?.message || '조회에 실패했습니다.');
      const nextRows = result.data?.data?.rows || [];
      setRows(nextRows);
      setDrafts(Object.fromEntries(nextRows.map((row) => [row.year_month, toDraft(row)])));
    } catch (error) {
      showToast(error.message || '조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentYm, showToast]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  function updateDraft(ym, field, value) {
    setDrafts((prev) => ({
      ...prev,
      [ym]: { ...(prev[ym] || {}), [field]: value },
    }));
  }

  async function runRowAction(ym, action) {
    if (!action) return;
    // 확정 취소는 한 번 더 묻는다 (급여 확정 취소와 같은 방식, 2026-10-02)
    if (action === 'reopen' && !window.confirm(`${ym} 확정을 취소할까요?\n입력칸을 다시 고칠 수 있게 됩니다.`)) return;
    const draft = drafts[ym] || {};
    const busy = `${ym}:${action}`;
    setBusyKey(busy);
    try {
      const paths = {
        save: `/api/admin/promotional-monthly-settlements/${ym}`,
        confirm: `/api/admin/promotional-monthly-settlements/${ym}/confirm`,
        reopen: `/api/admin/promotional-monthly-settlements/${ym}/reopen`,
        settle: `/api/admin/promotional-monthly-settlements/${ym}/settle`,
      };
      const result = action === 'save'
        ? await apiClient.put(paths.save, draft)
        : await apiClient.post(paths[action], action === 'confirm' ? draft : {});
      if (!result.ok) throw new Error(result.data?.message || '처리에 실패했습니다.');
      const labels = { save: '저장', confirm: '확정', reopen: '확정 취소', settle: '정산완료' };
      showToast(`${ym} ${labels[action]} 완료`, 'success');
      await loadRows();
    } catch (error) {
      showToast(error.message || '처리에 실패했습니다.', 'error');
    } finally {
      setBusyKey('');
    }
  }

  // 페이지 헤더 — 직원 관리 표준 (designStandard). 오른쪽 = 마곡 마케터 설정 (PC·휴대폰 공통)
  const header = (
    <div style={stdHeaderRow(isMobile)}>
      <div>
        <h2 style={stdPageTitle}>홍보물월정산</h2>
        <div style={stdPageSubtitle}>홍보물 월별 정산을 관리합니다.</div>
      </div>
      <div>
        <button type="button" style={{ ...stdOutlineBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setMagokOpen(true)}>
          <Settings size={14} />
          마곡 마케터 설정
        </button>
      </div>
    </div>
  );
  const magokModal = (
    <PromotionalMonthlyMagokModal
      open={magokOpen}
      onClose={() => setMagokOpen(false)}
      onSaved={loadRows}
      showToast={showToast}
      startYm={SETTLEMENT_START_MONTH}
      currentYm={currentYm}
    />
  );

  if (isMobile) {
    return (
      <div className="pms-page">
        {header}
        <PromotionalMonthlySettlementMobile
          rows={rows}
          drafts={drafts}
          loading={loading}
          busyKey={busyKey}
          updateDraft={updateDraft}
          runRowAction={runRowAction}
        />
        {magokModal}
      </div>
    );
  }

  return (
    <div className="pms-page">
      {header}

      <div className="pms-table-wrap">
        <table className="pms-table">
          <colgroup>
            <col className="pms-col-month" />
            <col className="pms-col-count" />
            <col className="pms-col-money" />
            <col className="pms-col-money" />
            <col className="pms-col-count" />
            <col className="pms-col-input" />
            <col className="pms-col-input" />
            <col className="pms-col-input-sm" />
            <col className="pms-col-input" />
            <col className="pms-col-input-sm" />
            <col className="pms-col-total" />
            <col className="pms-col-total" />
            <col className="pms-col-action" />
          </colgroup>
          <thead>
            <tr>
              <th>년-월</th>
              <th>영화</th>
              <th>홍보물</th>
              <th>택배</th>
              <th>마곡장수</th>
              <th>관리비</th>
              <th>회식*(1/2)</th>
              <th>유심</th>
              <th>영업*1.1</th>
              <th>기타</th>
              <th>샘이콜론</th>
              <th>파인그룹</th>
              <th>처리</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.year_month] || toDraft(row);
              const locked = row.is_confirmed || row.is_settled;
              const confirmBtn = confirmButtonState(row, busyKey);
              return (
                <tr key={row.year_month} className={locked ? 'is-locked' : ''}>
                  <td className="pms-month">{row.year_month}</td>
                  <td>{formatNumber(row.movie_ticket_count)}</td>
                  <td><ReadMoney value={row.promotional_sales} /></td>
                  <td><ReadMoney value={row.delivery_total} /></td>
                  <td>{formatNumber(row.magok_ticket_count)}</td>
                  {MONEY_FIELDS.map((field) => (
                    <td key={field}>
                      <MoneyInput
                        value={draft[field] || ''}
                        disabled={locked}
                        onChange={(value) => updateDraft(row.year_month, field, value)}
                      />
                    </td>
                  ))}
                  <td><ReadMoney value={row.samicolon_amount} /></td>
                  <td><ReadMoney value={row.pinegroup_amount} /></td>
                  <td>
                    <div className="pms-actions">
                      <button
                        type="button"
                        className="pms-action-btn save"
                        onClick={() => runRowAction(row.year_month, 'save')}
                        disabled={locked || busyKey === `${row.year_month}:save`}
                      >
                        <Save size={13} />
                        저장
                      </button>
                      <button
                        type="button"
                        className="pms-action-btn confirm"
                        onClick={() => runRowAction(row.year_month, confirmBtn.action)}
                        disabled={confirmBtn.disabled}
                        title={confirmBtn.title}
                      >
                        <CheckCircle2 size={13} />
                        {confirmBtn.label}
                      </button>
                      <button
                        type="button"
                        className="pms-action-btn settle"
                        onClick={() => runRowAction(row.year_month, 'settle')}
                        disabled={!row.is_confirmed || row.is_settled || busyKey === `${row.year_month}:settle`}
                      >
                        <LockKeyhole size={13} />
                        {row.is_settled ? '완료됨' : '정산완료'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 ? (
              <tr><td colSpan="13" className="pms-empty">조회된 월이 없습니다.</td></tr>
            ) : null}
            {loading ? (
              <tr><td colSpan="13" className="pms-empty">조회 중...</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {magokModal}
    </div>
  );
}
